import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, TrendingUp, Trophy, Users, Shield, DollarSign, BarChart3, Zap, FileText } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const News = () => {
  const news = [
    {
      id: 'nouvelles-reglementations-anj-2025',
      title: 'Nouvelles réglementations ANJ : ce qui change en 2025',
      excerpt: 'L\'Autorité Nationale des Jeux annonce de nouvelles mesures pour renforcer la protection des joueurs et améliorer la transparence des opérateurs.',
      date: '25.11.2025',
      category: 'Réglementation',
      icon: TrendingUp,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      id: 'tendances-paris-sportifs-2025-2026',
      title: 'Les tendances des paris sportifs pour la saison 2025-2026',
      excerpt: 'Analyse des sports les plus populaires et des nouveaux marchés qui attirent les parieurs français cette saison.',
      date: '24.11.2025',
      category: 'Tendances',
      icon: Trophy,
      color: 'bg-yellow-100 text-yellow-600',
    },
    {
      id: 'operateurs-ameliorent-experience-mobile',
      title: 'Comment les opérateurs améliorent l\'expérience mobile',
      excerpt: 'Focus sur les innovations technologiques qui rendent les applications de paris plus rapides et intuitives pour les utilisateurs.',
      date: '23.11.2025',
      category: 'Technologie',
      icon: Users,
      color: 'bg-green-100 text-green-600',
    },
    {
      id: 'bonus-bienvenue-evolution-2025',
      title: 'L\'évolution des bonus de bienvenue en 2025',
      excerpt: 'Analyse des nouvelles tendances dans les offres promotionnelles et comment les parieurs peuvent en profiter intelligemment.',
      date: '22.11.2025',
      category: 'Promotions',
      icon: DollarSign,
      color: 'bg-purple-100 text-purple-600',
    },
    {
      id: 'securite-donnees-paris-en-ligne',
      title: 'Sécurité des données : les nouvelles normes pour 2025',
      excerpt: 'Les opérateurs renforcent leurs mesures de protection des données personnelles face aux nouvelles menaces cybernétiques.',
      date: '21.11.2025',
      category: 'Sécurité',
      icon: Shield,
      color: 'bg-red-100 text-red-600',
    },
    {
      id: 'croissance-paris-esport-france',
      title: 'La croissance explosive des paris sur l\'e-sport en France',
      excerpt: 'Les paris sur les compétitions e-sport gagnent du terrain et deviennent un marché majeur pour les opérateurs français.',
      date: '20.11.2025',
      category: 'E-sport',
      icon: Zap,
      color: 'bg-indigo-100 text-indigo-600',
    },
    {
      id: 'strategies-parieurs-professionnels',
      title: 'Les stratégies des parieurs professionnels révélées',
      excerpt: 'Décryptage des techniques utilisées par les parieurs les plus performants pour maximiser leurs gains sur le long terme.',
      date: '19.11.2025',
      category: 'Stratégies',
      icon: BarChart3,
      color: 'bg-teal-100 text-teal-600',
    },
    {
      id: 'impact-intelligence-artificielle-paris',
      title: 'L\'impact de l\'intelligence artificielle sur les paris en ligne',
      excerpt: 'Comment l\'IA transforme l\'expérience des parieurs et aide les opérateurs à proposer des services plus personnalisés.',
      date: '18.11.2025',
      category: 'Innovation',
      icon: FileText,
      color: 'bg-pink-100 text-pink-600',
    },
    {
      id: 'retraits-rapides-nouvelles-technologies',
      title: 'Retraits ultra-rapides : les nouvelles technologies au service des parieurs',
      excerpt: 'Les opérateurs investissent dans des solutions de paiement innovantes pour réduire les délais de retrait à quelques heures.',
      date: '17.11.2025',
      category: 'Paiements',
      icon: DollarSign,
      color: 'bg-cyan-100 text-cyan-600',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Actualités et analyses
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Restez informé des dernières nouveautés, réglementations et tendances du secteur des paris en ligne en France
          </p>
        </div>

        {/* News Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {news.map((item, index) => {
            const Icon = item.icon;
            const newsImages = [
              "/images/courtney-schroedel-v1SS8Wiz-uI-unsplash.webp",
              "/images/fauzan-saari-AmhdN68wjPc-unsplash.webp",
              "/images/guzman-barquin-GQmYqY2ySLg-unsplash.webp",
              "/images/horserat-Iuhme32mv9I-unsplash.webp",
              "/images/josh-calabrese-zcYRw547Dps-unsplash.webp",
              "/images/kevin-mueller-Q-fL04RhuMg-unsplash.webp",
              "/images/konstantin-evdokimov-JP0qiWQzjrg-unsplash.webp",
              "/images/lesly-juarez-isWEKdSRCrA-unsplash.webp",
              "/images/paulo-henrique-macedo-dias-1v7axvhwnOU-unsplash.webp"
            ];
            return (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                data-discover="true"
                itemScope
                itemType="https://schema.org/NewsArticle"
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-primary-red group"
              >
                {/* Image */}
                <div className="h-48 relative overflow-hidden">
                  <img 
                    src={newsImages[index]} 
                    alt={item.title}
                    itemProp="image"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="absolute top-4 left-4">
                    <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold ${item.color} backdrop-blur-sm bg-white/90`}>
                      <Icon size={14} />
                      <span>{item.category}</span>
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <Calendar size={16} className="mr-2" />
                    <time itemProp="datePublished" dateTime={item.date.split('.').reverse().join('-')}>{item.date}</time>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-primary-red transition-colors" itemProp="headline">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3" itemProp="description">
                    {item.excerpt}
                  </p>

                  <div className="inline-flex items-center text-primary-red font-semibold">
                    Lire la suite
                    <ArrowRight className="ml-2" size={18} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* How We Choose News Section */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 md:p-12 mb-12 shadow-lg border border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Comment nous sélectionnons nos actualités
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Dans un secteur en constante évolution comme les paris en ligne, l'information est cruciale. 
              Mais toutes les informations ne se valent pas. Chez ParisPari Hub, nous appliquons une 
              méthodologie rigoureuse pour sélectionner et présenter les actualités qui méritent vraiment 
              votre attention.
            </p>
            <p>
              Notre premier critère est la pertinence pour les parieurs français. Nous ne nous contentons 
              pas de reprendre des nouvelles générales : nous nous concentrons sur ce qui impacte réellement 
              votre expérience de paris en ligne. Une nouvelle réglementation de l'ANJ, une évolution des 
              bonus, une innovation technologique qui améliore l'expérience utilisateur : voilà le type 
              d'informations que nous privilégions.
            </p>
            <p>
              La fiabilité des sources est essentielle. Nous ne relayons que des informations provenant 
              de sources vérifiables : communiqués officiels de l'ANJ, annonces des opérateurs agréés, 
              analyses d'experts reconnus. Nous évitons les rumeurs, les spéculations non fondées, et les 
              informations provenant de sources douteuses. Votre confiance est précieuse, et nous la 
              méritons en étant rigoureux.
            </p>
            <p>
              L'impact pratique est aussi un critère clé. Une nouvelle qui change réellement la façon 
              dont vous pariez en ligne mérite d'être mise en avant. Une simple mise à jour mineure d'une 
              application, en revanche, ne justifie pas nécessairement un article complet. Nous privilégions 
              les actualités qui vous aident à mieux comprendre le secteur, à optimiser votre stratégie, ou 
              à éviter les pièges.
            </p>
            <p>
              Nous analysons aussi les tendances à long terme. Au-delà de l'actualité immédiate, nous 
              identifions les évolutions structurelles du secteur : croissance de certains types de paris, 
              nouvelles habitudes des parieurs, innovations technologiques qui s'imposent. Ces analyses 
              vous donnent une vision plus large et vous aident à anticiper les changements.
            </p>
            <p>
              Chaque actualité que nous publions est analysée, vérifiée, et mise en contexte. Nous ne nous 
              contentons pas de relayer une information : nous l'expliquons, nous analysons ses implications, 
              et nous vous donnons notre avis éclairé. Notre objectif n'est pas de vous noyer sous une 
              masse d'informations, mais de vous donner les clés pour comprendre ce qui se passe vraiment 
              dans le secteur des paris en ligne en France.
            </p>
            <p>
              Enfin, nous restons indépendants. Aucun opérateur ne peut influencer notre sélection d'actualités 
              ou notre analyse. Si une nouvelle est négative pour un opérateur mais importante pour les 
              parieurs, nous la publions. Si une évolution réglementaire peut sembler contraignante mais 
              est en réalité protectrice, nous l'expliquons clairement. Votre intérêt passe avant tout.
            </p>
            <p>
              Cette approche rigoureuse garantit que chaque actualité que vous lisez sur ParisPari Hub 
              est pertinente, fiable, et utile. Nous ne cherchons pas à publier le plus d'articles possible, 
              mais à publier les meilleurs articles possibles. Votre temps est précieux, et nous le 
              respectons en ne vous proposant que des contenus qui en valent vraiment la peine.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-primary-red to-primary-red-dark rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Ne manquez aucune actualité
          </h2>
          <p className="text-xl mb-6 opacity-90">
            Consultez régulièrement cette page pour rester informé des dernières évolutions du secteur
          </p>
          <Link
            to="/"
            className="inline-flex items-center bg-white text-primary-red px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Retour à l'accueil
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default News;
